import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import {
  ChangeLanguageIcon,
  LogoutIcon,
  NotificationIcon,
  RoleIcon,
  SupportDudeIcon,
  UserIcon,
} from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import NavigationButton from '../ui/NavigationButton';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout, setIsAdvancedModeEnabled } from '../../store/general/general-slice';
import User from '../../models/general/user';
import { resetOrganizationState } from '../../store/organization/organization-slice';
import { resetRobotsState } from '../../store/robots/robots-slice';
import SupportDudePopover from './SupportDudePopover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import { Theme, useMediaQuery } from '@mui/material';
import RoleTopBarPopover from './RoleTopBarPopover';
import { selectCurrentUserRole } from '../../store/general/general-slice-selectors';
import { FilterKeys } from '@common/enums/query-params/filters';
import { useSearchParams } from 'react-router-dom';
import i18next from 'i18next';
import { availableApiLanguages, STORED_LANG_KEY } from '../../i18n';
import OrganizationRole from '../../models/enums/organization-roles';
import { UserOverlayTabs } from './UserOverlay';
import { patchUserById } from '../../store/organization/organization-actions';
import UserPatchType from '../../models/enums/user-patch-type';
import { Language } from '../../models/enums/languages';
import {
  selectIsAdvancedModeEnabled,
  selectIsReadOnly,
} from '@common/store/app-configurations/app-configurations-selectors';
import { useSharedSelector } from '@common/store/SharedProvider';
import { resetAnalyticsUser } from 'services/analytics';

const UserTopBarPopover: React.FC<{
  user: User;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
}> = ({ user, anchorEl, onClose }) => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const userRole = useAppSelector((state) => selectCurrentUserRole(state));

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));

  const [supportDudeAnchorEl, setSupportDudeAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState<null | HTMLButtonElement>(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState<HTMLButtonElement | null>(null);

  const dispatch = useAppDispatch();

  const logoutClickHandler = () => {
    dispatch(logout());
    dispatch(resetOrganizationState());
    dispatch(resetRobotsState());
    resetAnalyticsUser();
  };

  const navigateToUserClickHandler = () => {
    onClose();
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.UserId, user.id);
      return prevState;
    });
  };

  const openSupportDudePopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSupportDudeAnchorEl(event.currentTarget);
  };

  const closeSupportDudePopoverHandler = () => {
    setSupportDudeAnchorEl(null);
  };

  const openRolePopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRoleAnchorEl(event.currentTarget);
  };

  const closeRolePopoverHandler = () => {
    setRoleAnchorEl(null);
  };

  const languageSelectClickHandler = (languageCode: string) => {
    setLanguageAnchorEl(null);
    i18next.changeLanguage(languageCode, (err, t) => {
      if (err) {
        return console.log('language could not be changed', err);
      } else {
        localStorage.setItem(STORED_LANG_KEY, languageCode);
        dispatch(
          patchUserById({
            id: user.id,
            patchType: UserPatchType.GeneralPatch,
            data: { language: languageCode as Language },
          }),
        );
      }
    });
  };

  const openLanguageSelectHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const closeLanguageSelectHandler = () => {
    setLanguageAnchorEl(null);
  };

  const navigateToNotificationsClickHandler = () => {
    onClose();
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.UserId, user.id);
      prevState.set(FilterKeys.Tab, UserOverlayTabs.Notifications);
      return prevState;
    });
  };

  const onAdvancedModeEnabledToggleChange = () => {
    dispatch(setIsAdvancedModeEnabled(!isAdvancedModeEnabled));
  };

  return (
    <React.Fragment>
      <Popover
        id="user-popover-id"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack>
          <Stack sx={{ p: 2 }} direction="row" spacing={1} alignItems="center">
            <UserIcon fontSize="large" color="action" />
            <Stack>
              <Typography variant="body2" whiteSpace="nowrap">
                {user.name}
              </Typography>
              <Typography variant="subtitle2" whiteSpace="nowrap">
                {user.email}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <NavigationButton
            isNavOpen={true}
            title={t('button-profile')}
            icon={<UserIcon />}
            onClick={navigateToUserClickHandler}
          />
          <NavigationButton
            isNavOpen={true}
            title={t('button-notification-settings')}
            lowerCase
            icon={<NotificationIcon />}
            onClick={navigateToNotificationsClickHandler}
          />
          {matchesSmDown && user.organization.role === OrganizationRole.MANUFACTURER && (
            <NavigationButton
              isNavOpen={true}
              title={`${t('label-current-role')}: ${t('user-role-' + userRole)}`}
              icon={<RoleIcon />}
              onClick={openRolePopoverClickHandler}
            />
          )}
          <NavigationButton
            isNavOpen={true}
            title={t('button-language')}
            icon={<ChangeLanguageIcon />}
            onClick={openLanguageSelectHandler}
          />
          <Menu
            id="fade-menu"
            MenuListProps={{
              'aria-labelledby': 'fade-button',
            }}
            anchorEl={languageAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={languageAnchorEl != null}
            onClose={closeLanguageSelectHandler}
          >
            {availableApiLanguages.map((lang) => (
              <MenuItem
                key={lang.languageCode}
                value={lang.languageCode}
                onClick={() => languageSelectClickHandler(lang.languageCode)}
              >
                {lang.description}
              </MenuItem>
            ))}
          </Menu>

          <NavigationButton
            isNavOpen={true}
            title={t('advanced-mode')}
            lowerCase
            disabled={{ disabled: isReadOnly, tooltip: t('advanced-mode-requires-authentication') }}
            icon={<Switch size="small" checked={isAdvancedModeEnabled} sx={{ mx: -1 }} />} // negative margin required to make up for the increase switch size
            onClick={onAdvancedModeEnabledToggleChange}
          />
          {matchesSmDown && (
            <NavigationButton
              isNavOpen={true}
              title={t('title-contact-support')}
              icon={<SupportDudeIcon />}
              onClick={openSupportDudePopoverClickHandler}
            />
          )}

          <Divider />
          <NavigationButton
            isNavOpen={true}
            title={t('button-logout')}
            icon={<LogoutIcon />}
            onClick={logoutClickHandler}
          />
        </Stack>
      </Popover>
      <SupportDudePopover anchorEl={supportDudeAnchorEl} onClose={closeSupportDudePopoverHandler} />
      <RoleTopBarPopover user={user} anchorEl={roleAnchorEl} onClose={closeRolePopoverHandler} />
    </React.Fragment>
  );
};

export default UserTopBarPopover;
